/**
 * Mobile-optimized flyer drop logger — admin walks neighborhood with phone, taps in.
 * Lives at /admin/flyer-drop (separate from /admin so it's mobile-first).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Brand";
import { toast } from "sonner";

export default function FlyerDrop() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [zip, setZip] = useState("80207");
  const [neighborhood, setNeighborhood] = useState("");
  const [count, setCount] = useState(50);
  const [deliveredBy, setDeliveredBy] = useState("");
  const [notes, setNotes] = useState("");
  const [geo, setGeo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login?next=/admin/flyer-drop");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/flyer-drops").then(({ data }) => setRecent((data.items || []).slice(0, 5)));
    }
  }, [user]);

  const grabLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) });
        toast.success("Location pinned");
      },
      () => toast.error("Couldn't grab location — enable in browser settings"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/flyer-drops", {
        drop_date: new Date().toISOString().slice(0, 10),
        zip_code: zip,
        neighborhood,
        flyer_count: parseInt(count) || 0,
        delivered_by: deliveredBy || (user?.name ?? null),
        notes,
        geo,
      });
      toast.success(`${count} flyers logged in ${zip}`);
      setNotes("");
      setNeighborhood("");
      setGeo(null);
      const { data } = await api.get("/admin/flyer-drops");
      setRecent((data.items || []).slice(0, 5));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-brand-slate flex flex-col" data-testid="flyer-drop-page">
      <header className="px-5 py-4 flex items-center justify-between border-b border-slate-100 bg-white sticky top-0 z-10">
        <BrandMark />
        <Link
          to="/admin"
          className="text-[10px] font-black uppercase tracking-widest text-brand-red"
          data-testid="flyer-back-admin"
        >
          ← Admin
        </Link>
      </header>

      <main className="flex-1 px-5 py-6 max-w-md mx-auto w-full">
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
          Field Marketing
        </p>
        <h1 className="text-3xl font-black uppercase tracking-tightest text-brand-blue leading-tight">
          Flyer Drop Logger
        </h1>
        <p className="text-[12px] font-bold text-slate-500 mt-1.5 leading-snug">
          Tap-to-log every drop while you're walking. Pin location with one tap.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="flyer-form">
          <div className="grid grid-cols-2 gap-2.5">
            <FieldInput label="ZIP" value={zip} onChange={setZip} testId="flyer-zip" />
            <FieldInput label="Count" type="number" value={count} onChange={setCount} testId="flyer-count" />
          </div>
          <FieldInput
            label="Neighborhood"
            value={neighborhood}
            onChange={setNeighborhood}
            placeholder="e.g. Park Hill, City Park, Stapleton"
            testId="flyer-neighborhood"
          />
          <FieldInput
            label="Delivered By"
            value={deliveredBy}
            onChange={setDeliveredBy}
            placeholder={user.name || "Your name"}
            testId="flyer-by"
          />

          <button
            type="button"
            onClick={grabLocation}
            className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] border-2 transition-colors ${
              geo
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-white border-slate-200 text-brand-blue hover:bg-brand-blue hover:text-white"
            }`}
            data-testid="flyer-geo-button"
          >
            {geo ? (
              <><i className="fas fa-location-dot mr-2" /> Pinned: {geo.lat}, {geo.lng}</>
            ) : (
              <><i className="fas fa-crosshairs mr-2" /> Pin Current Location</>
            )}
          </button>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1.5">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Streets covered, missed houses, dog at #14…"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
              data-testid="flyer-notes"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full py-4 rounded-xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
            data-testid="flyer-submit"
          >
            {busy ? (
              <><i className="fas fa-spinner fa-spin mr-2" /> Logging</>
            ) : (
              <><i className="fas fa-plus mr-2" /> Log Drop</>
            )}
          </button>
        </form>

        {recent.length > 0 && (
          <section className="mt-8" data-testid="flyer-recent">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">
              Last 5 Drops
            </p>
            <div className="space-y-2">
              {recent.map((d) => (
                <div
                  key={d.id}
                  className="bg-white rounded-2xl border border-slate-100 px-3 py-2.5 flex items-center gap-3"
                  data-testid={`flyer-recent-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center font-black text-xs">
                    {d.flyer_count}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-xs uppercase text-brand-blue tracking-tightest truncate">
                      {d.neighborhood || d.zip_code}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500">
                      {d.zip_code} · {d.drop_date} · {d.delivered_by || "—"}
                    </p>
                  </div>
                  {d.geo && <i className="fas fa-location-dot text-emerald-500" />}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-base font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
        data-testid={testId}
      />
    </div>
  );
}
